import React, { useState, useEffect } from "react";
import { useLocation } from 'react-router-dom';
import styles from "./PageEdit.module.css"

// Util
import { fetchData } from "../util/fetchData";
import { saveData } from "../util/saveJsonData";
import { handleFieldChange } from "../util/handleFieldChange";
import Logout from "../util/Logout"


/**
 * A reusable page editor component that handles state, fetching, and saving.
 * It displays its child components dynamically, injecting Data and updateField 
 * in them, based on their section key, which matches the json file.
 * Use this component to wrap the elements on the page editor pages, like in the example.
 *
 * @param {Object} props - Component properties.
 * @param {string} props.apiPath - The API path for fetching data (e.g., "juniorTeen").
 * @param {React.ReactNode} props.children - Components that will be displayed inside EditPage.
 * @param {Function} fetchFunc - The provided function is used to fetch data, it defaults to fetchData.
 * @param {Function} saveFunc - The provided function is used to save data, it defaults to saveData.
 * @example
 * <EditPage apiPath="page/juniorTeen.js", requiredGoogleAuth=true, returnTo=/junior-teen>
 *   <PageHeaderEdit sectionKey="PageHeader" />
 *   <PageInfoEdit sectionKey="Sec1" />
 * </EditPage>
 */
export default function EditPage({ apiPath, children}) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState("");
    const [modal, setModal] = useState(false);
    const location = useLocation();

    useEffect(() => {
    fetchData(apiPath, setData, setLoading);
  }, [apiPath, location.pathname]);

    useEffect(() => {
        fetchData(apiPath, setData, setLoading);
    }, [apiPath]);

    const updateField = handleFieldChange(setData);

    const handleSave = () => {
        saveData(apiPath, data, setStatus);
        toggleModal();
    };

    const toggleModal = () => {
        setModal(true)
        setTimeout(() => {
            setModal(false);
            setStatus("");
        }, 5000);
    }

    if (loading) return <p>Loading...</p>;

    return (
        <div className={styles.editPage}>

            <Logout/>
            
            {/* Inject `data` and `updateField` into each child component based on their section keys*/}
            {React.Children.map(children, (child) =>
                child.props.sectionKey
                    ? React.cloneElement(child, { 
                        ...data[child.props.sectionKey], 
                        updateField 
                    })
                    : child
            )}

            {/* Save Button */}
            <button onClick={handleSave} className={styles.save}>Save</button>

            {/* Status Message */}
            {modal && 
                <div className={styles.modal}>
                    <p>{status}</p>
                </div>}
        </div>
    );
}
